import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/site/Layout";
import Breadcrumb from "@/components/site/Breadcrumb";
import { liveFeed, type LiveItem } from "@/mocks/live";
import { newsCategories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

function LivePage() {
  const [scope, setScope] = useState("全部");
  const [onlyImportant, setOnlyImportant] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const scopes = useMemo(() => Array.from(new Set(liveFeed.map((i) => i.scope))), []);

  const filtered: LiveItem[] = liveFeed
    .filter((i) => (scope === "全部" ? true : i.scope === scope))
    .filter((i) => (onlyImportant ? i.isImportant : true))
    .slice()
    .reverse();

  usePageMeta({
    title: "直播滚动更新 - 全天实时快讯，吃瓜新闻速递频道",
    description:
      "吃瓜新闻直播滚动更新：实时快讯、重要消息与早晚报内容全天滚动推送，可按栏目筛选、只看重要，掌握每一条新鲜动态。",
    canonical: "https://youbijing.com.cn/live",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://youbijing.com.cn/" },
          { "@type": "ListItem", position: 2, name: "直播滚动更新", item: "https://youbijing.com.cn/live" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LiveBlogPosting",
        name: "吃瓜新闻全天直播快讯",
        coverageStartTime: "2026-09-07T09:02:00+08:00",
        liveBlogUpdate: liveFeed.slice(0, 3).map((i) => ({
          "@type": "BlogPosting",
          headline: i.text,
          datePublished: `2026-09-07T${i.time}:00+08:00`,
        })),
      },
    ],
  });

  const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
    now.getSeconds(),
  ).padStart(2, "0")}`;

  return (
    <Layout>
      {/* 直播头 */}
      <div className="bg-primary-600 text-background-50">
        <div className="mx-auto max-w-6xl px-4 py-7 md:px-6 md:py-9">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "直播滚动更新" }]} />
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <span className="h-3.5 w-3.5 rounded-full bg-accent-400 animate-pulse-dot" />
                </span>
                <h1 className="font-heading text-3xl font-black md:text-4xl">直播滚动更新</h1>
                <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold">LIVE</span>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-6 text-background-50/85">
                全天快讯实时滚动，重要消息与早晚报速递不停歇。今日已累计更新 {liveFeed.length} 条，最后更新于 13:02。
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-background-50/70">页面时钟</p>
              <p className="font-mono text-3xl font-bold tabular-nums md:text-4xl">{clock}</p>
              <p className="mt-1 text-xs text-background-50/70">2026年9月7日 星期一</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* 筛选栏 */}
            <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-1.5 rounded-full bg-background-100 p-1">
                {["全部", ...scopes].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      scope === s ? "bg-primary-500 font-medium text-background-50" : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {s === "全部" ? "全部" : `${s}快讯`}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setOnlyImportant((v) => !v)}
                className={`inline-flex items-center gap-2 self-start whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors ${
                  onlyImportant
                    ? "border-primary-400 bg-primary-100/70 text-primary-700"
                    : "border-background-200 text-foreground-600 hover:border-primary-300"
                }`}
              >
                <i className={`ri-vip-crown-2-line ${onlyImportant ? "text-primary-500" : "text-foreground-400"}`} />
                只看重要
              </button>
            </div>

            {/* 时间线 */}
            <ol className="mt-2 space-y-0">
              {filtered.map((item, i) => (
                <li key={item.id} className="relative flex gap-4 pb-2">
                  <div className="flex w-20 shrink-0 justify-end pt-5">
                    <span className="font-mono text-sm font-semibold tabular-nums text-accent-700">{item.time}</span>
                  </div>
                  <div className="relative flex flex-col items-center">
                    {i === 0 ? (
                      <span className="relative mt-5 flex h-3 w-3 items-center justify-center">
                        <span className="h-3 w-3 rounded-full bg-primary-500" />
                        <span className="absolute h-3 w-3 rounded-full bg-primary-500 opacity-60 animate-ping" />
                      </span>
                    ) : (
                      <span className={`mt-5 h-3 w-3 rounded-full ${item.isImportant ? "bg-accent-500" : "bg-background-300"}`} />
                    )}
                    <span className="w-px flex-1 bg-background-200" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1 pb-5 pt-3">
                    <div
                      className={`rounded-r-lg rounded-tl-lg p-4 transition-colors ${
                        item.isImportant
                          ? "border-l-2 border-accent-500 bg-accent-100/70"
                          : i === 0
                            ? "border-l-2 border-primary-500 bg-background-100"
                            : "border-l-2 border-background-300 bg-background-50"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-md bg-background-50 px-2 py-0.5 text-xs font-medium text-secondary-900">
                          {item.scope}
                        </span>
                        {item.isImportant && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-800">
                            <i className="ri-star-fill" /> 重要
                          </span>
                        )}
                        {i === 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse-dot" /> 最新一条
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm leading-6 text-foreground-800">{item.text}</p>
                    </div>
                  </div>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="rounded-xl border border-dashed border-background-200 py-16 text-center text-sm text-foreground-500">
                  当前条件下没有快讯，试试切换筛选。
                </li>
              )}
            </ol>
          </div>

          {/* 右栏 */}
          <aside className="space-y-8" aria-label="直播统计">
            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">今日战报</h3>
              <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-background-100 py-3">
                  <dt className="text-xs text-foreground-500">累计条数</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-primary-600">{liveFeed.length}</dd>
                </div>
                <div className="rounded-lg bg-background-100 py-3">
                  <dt className="text-xs text-foreground-500">重要消息</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-accent-600">
                    {liveFeed.filter((i) => i.isImportant).length}
                  </dd>
                </div>
                <div className="rounded-lg bg-background-100 py-3">
                  <dt className="text-xs text-foreground-500">覆盖栏目</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-secondary-600">{scopes.length}</dd>
                </div>
              </dl>
            </section>

            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">快速看栏目</h3>
              <ul className="mt-4 space-y-1">
                {newsCategories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/category/${c.slug}`}
                      className="group flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-2.5 text-sm text-foreground-700 group-hover:text-primary-700">
                        <i className={`${c.icon} text-primary-500`} /> {c.name}快讯
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <Link
              to="/"
              className="flex items-center justify-between rounded-xl bg-accent-500 p-5 text-background-50 transition-colors hover:bg-accent-600"
            >
              <span>
                <span className="block font-heading text-base font-bold">回到今日要闻</span>
                <span className="mt-1 block text-sm text-background-50/85">早报晚报与热点速览一次看够</span>
              </span>
              <i className="ri-arrow-right-line text-xl" />
            </Link>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

export default LivePage;